import { Feature } from "./features";
import { IpaType, ipaToFeatures } from "./ipa";

export type FeatureOptions = Feature | Feature[];

/**
 * give it the features you want in columns, rows, & cells of a table
 * plus the IPA symbols to consider (that have associated features),
 * and it will figure out the symbol that goes in each (column, row, cell)
 */
export class BucketByFeatures {
  private readonly lookup: IpaType[][][] = [];

  constructor(symbols: IpaType[],
    columns: FeatureOptions[], rows: FeatureOptions[], cells: FeatureOptions[]
  ) {
    this.registerAll(symbols, columns, rows, cells);
  }

  /** get the symbol in the given column, row, & cell, if any */
  fetch(column: number, row?: number, cell?: number): IpaType | undefined {
    if (!this.lookup[column] || !this.lookup[column][row ?? 0]) {
      return undefined;
    }
    return this.lookup[column][row ?? 0][cell ?? 0];
  }

  /** figure out which symbols are associated with the different parts of the table */
  private registerAll(symbols: IpaType[], columns: FeatureOptions[], rows: FeatureOptions[], cells: FeatureOptions[]) {
    for (let x = 0; x < columns.length; x++) {
      const colFeatures = columns[x];
      if (rows.length === 0) {
        // no rows, so store just by column
        const allFeatures = this.combine(colFeatures);
        this.add(symbols, allFeatures, x, 0, 0);
      }
      for (let y = 0; y < rows.length; y++) {
        const rowFeatures = rows[y];
        let foundAny = false;
        for (let z = 0; z < cells.length; z++) {
          const cellFeatures = cells[z];
          const allFeatures = this.combine(colFeatures, rowFeatures, cellFeatures);
          foundAny = this.add(symbols, allFeatures, x, y, z) || foundAny;
        }
        if (!foundAny) {
          // either there are no cell features or no symbols had features
          // from (col, row, cell), so check just (col, row) features
          const allFeatures = this.combine(colFeatures, rowFeatures);
          this.add(symbols, allFeatures, x, y, 0)
        }
      }
    }
  }

  private combine(f1: FeatureOptions, f2?: FeatureOptions, f3?: FeatureOptions): Feature[] {
    let features: Feature[] = [];
    if (!f2) {
      return features.concat(f1);
    } else if (!f3) {
      return features.concat(f1).concat(f2);
    }
    return features.concat(f1).concat(f2).concat(f3);
  }

  /** find the first symbol with all of 'features' and store it in (column, row, cell) */
  private add(
    symbols: IpaType[], features: Feature[],
    column: number, row: number, cell: number
  ): boolean {
    for (const p of symbols) {
      const symbolFeatures = ipaToFeatures(p);
      if (symbolFeatures && features.every(f => symbolFeatures.includes(f))) {
        if (!this.lookup[column]) {
          this.lookup[column] = [];
        }
        if (!this.lookup[column][row]) {
          this.lookup[column][row] = [];
        }
        this.lookup[column][row][cell] = p;
        return true;
      }
    }
    return false;
  }
}
